/**
 * WidgetDockContext - Gerenciador de widgets dockados no TopBar
 *
 * Permite que widgets minimizados sejam exibidos como chips no TopBar,
 * com persistência de estado e posição via localStorage.
 *
 * OTIMIZADO para reduzir re-renders:
 * - Usa useRef para widgetData (não causa re-render)
 * - Separação de contextos para estado vs ações
 * - Throttle em updateWidgetData
 */

import React, {
 createContext, useContext, useState, useCallback, useMemo, useRef, useEffect 
} from 'react';

const STORAGE_KEY = 'widgetDock_state';

// Throttle helper - evita atualizações muito frequentes
const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => { inThrottle = false; }, limit);
    }
  };
};

// Configuração padrão dos widgets
const DEFAULT_WIDGET_CONFIG = {
  statusCounters: {
    id: 'statusCounters',
    label: 'Status',
    icon: 'DirectionsCar',
    defaultDocked: true,
    order: 0,
  },
  poiFilter: {
    id: 'poiFilter',
    label: 'POIs',
    icon: 'Place',
    defaultDocked: true,
    order: 1,
  },
  geofenceFilter: {
    id: 'geofenceFilter',
    label: 'Cercas',
    icon: 'CropFree',
    defaultDocked: true,
    order: 2,
  },
  speedometer: {
    id: 'speedometer',
    label: 'Velocidade',
    icon: 'Speed',
    defaultDocked: true, // Habilitado para dock
    order: 3,
  },
};

// Estado inicial - carrega do localStorage ou usa padrão
const getInitialState = () => {
  // Tentar recuperar estado salvo
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validar estrutura
      if (parsed.dockedWidgets && parsed.minimizedWidgets) {
        return {
          dockedWidgets: parsed.dockedWidgets,
          minimizedWidgets: parsed.minimizedWidgets,
          widgetData: {},
        };
      }
    }
  } catch (e) {
    // Ignore parsing errors
  }

  // Estado padrão - todos os widgets NÃO minimizados (expandidos)
  return {
    dockedWidgets: Object.values(DEFAULT_WIDGET_CONFIG)
      .filter(w => w.defaultDocked)
      .map(w => w.id),
    minimizedWidgets: [], // Nenhum minimizado por padrão
    widgetData: {},
  };
};

// Contexto
const WidgetDockContext = createContext(null);

// Provider
export const WidgetDockProvider = ({ children }) => {
  // Estado principal (dock/minimize) - causa re-render quando muda
  const [state, setState] = useState(getInitialState);

  // widgetData em useRef - NÃO causa re-render (dados como velocidade, contadores)
  const widgetDataRef = useRef({});

  // Persistir estado no localStorage quando mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        dockedWidgets: state.dockedWidgets,
        minimizedWidgets: state.minimizedWidgets,
      }));
    } catch (e) {
      // Ignore storage errors
    }
  }, [state.dockedWidgets, state.minimizedWidgets]);

  // Dockar um widget (adicionar ao TopBar)
  const dockWidget = useCallback((widgetId) => {
    setState(prev => {
      if (prev.dockedWidgets.includes(widgetId)) return prev;
      return {
        ...prev,
        dockedWidgets: [...prev.dockedWidgets, widgetId],
      };
    });
  }, []);

  // Undockar um widget (remover do TopBar)
  const undockWidget = useCallback((widgetId) => {
    setState(prev => ({
      ...prev,
      dockedWidgets: prev.dockedWidgets.filter(id => id !== widgetId),
    }));
  }, []);

  // Minimizar widget (mostrar como chip no dock)
  // Automatically dock the widget if not already docked
  const minimizeWidget = useCallback((widgetId) => {
    setState(prev => {
      if (prev.minimizedWidgets.includes(widgetId)) return prev;
      // Ensure widget is docked so it appears in TopBar
      const newDockedWidgets = prev.dockedWidgets.includes(widgetId)
        ? prev.dockedWidgets
        : [...prev.dockedWidgets, widgetId];
      return {
        ...prev,
        dockedWidgets: newDockedWidgets,
        minimizedWidgets: [...prev.minimizedWidgets, widgetId],
      };
    });
  }, []);

  // Expandir widget (remover do dock, mostrar flutuante)
  const expandWidget = useCallback((widgetId) => {
    setState(prev => ({
      ...prev,
      minimizedWidgets: prev.minimizedWidgets.filter(id => id !== widgetId),
    }));
  }, []);

  // Toggle dock state
  const toggleDock = useCallback((widgetId) => {
    setState(prev => {
      const isDocked = prev.dockedWidgets.includes(widgetId);
      if (isDocked) {
        return {
          ...prev,
          dockedWidgets: prev.dockedWidgets.filter(id => id !== widgetId),
        };
      }
      return {
        ...prev,
        dockedWidgets: [...prev.dockedWidgets, widgetId],
      };
    });
  }, []);

  // Toggle minimize state
  // When minimizing, automatically dock the widget so it appears in TopBar
  const toggleMinimize = useCallback((widgetId) => {
    setState(prev => {
      const isMinimized = prev.minimizedWidgets.includes(widgetId);
      if (isMinimized) {
        // Expanding - just remove from minimizedWidgets
        return {
          ...prev,
          minimizedWidgets: prev.minimizedWidgets.filter(id => id !== widgetId),
        };
      }
      // Minimizing - ensure widget is docked so it appears in TopBar
      const newDockedWidgets = prev.dockedWidgets.includes(widgetId)
        ? prev.dockedWidgets
        : [...prev.dockedWidgets, widgetId];
      return {
        ...prev,
        dockedWidgets: newDockedWidgets,
        minimizedWidgets: [...prev.minimizedWidgets, widgetId],
      };
    });
  }, []);

  // Atualizar dados de um widget (ex: contadores, velocidade)
  // Usa useRef para evitar re-renders frequentes
  // Os dados ficam disponíveis via getWidgetData() - sem causar re-render
  const updateWidgetDataRaw = useCallback((widgetId, data) => {
    widgetDataRef.current = {
      ...widgetDataRef.current,
      [widgetId]: {
        ...(widgetDataRef.current[widgetId] || {}),
        ...data,
      },
    };
    // Não causa re-render - chips acessam via getWidgetData()
  }, []);

  // Versão throttled para evitar atualizações muito frequentes (max 1x por 1000ms)
  const updateWidgetData = useMemo(
    () => throttle(updateWidgetDataRaw, 1000),
    [updateWidgetDataRaw]
  );

  // Lista de widgets para renderizar no dock (dockados + minimizados)
  // NOTA: Não depende de dataVersion - os chips acessam dados via getWidgetData()
  // Isso evita re-renders desnecessários quando apenas os dados mudam
  const dockedChips = useMemo(() => {
    return state.dockedWidgets
      .filter(id => state.minimizedWidgets.includes(id))
      .map(id => ({
        ...DEFAULT_WIDGET_CONFIG[id],
        // Não incluímos data aqui - chips usam getWidgetData() para dados em tempo real
      }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [state.dockedWidgets, state.minimizedWidgets]);

  // Getter para widgetData (acesso ao ref sem causar re-render)
  const getWidgetData = useCallback((widgetId) => {
    return widgetDataRef.current[widgetId] || {};
  }, []);

  // Value separado por categoria para evitar re-renders desnecessários
  const value = useMemo(() => ({
    // Estado (arrays/objetos diretos para acesso nos hooks)
    dockedWidgets: state.dockedWidgets,
    minimizedWidgets: state.minimizedWidgets,
    widgetDataRef, // Ref para acesso direto sem re-render
    getWidgetData, // Getter seguro
    dockedChips,

    // Ações (callbacks estáveis)
    dockWidget,
    undockWidget,
    minimizeWidget,
    expandWidget,
    toggleDock,
    toggleMinimize,
    updateWidgetData,

    // Config (estático)
    widgetConfig: DEFAULT_WIDGET_CONFIG,
  }), [
    state.dockedWidgets,
    state.minimizedWidgets,
    dockedChips,
    getWidgetData,
    dockWidget,
    undockWidget,
    minimizeWidget,
    expandWidget,
    toggleDock,
    toggleMinimize,
    updateWidgetData,
  ]);

  return (
    <WidgetDockContext.Provider value={value}>
      {children}
    </WidgetDockContext.Provider>
  );
};

// Hook para usar o contexto
export const useWidgetDock = () => {
  const context = useContext(WidgetDockContext);
  if (!context) {
    throw new Error('useWidgetDock must be used within WidgetDockProvider');
  }
  return context;
};

// Hook simplificado para um widget específico - OTIMIZADO
export const useWidgetDockState = (widgetId) => {
  const {
    dockedWidgets,
    minimizedWidgets,
    getWidgetData,
    toggleDock,
    toggleMinimize,
    updateWidgetData,
  } = useWidgetDock();

  // Memoizar valores derivados
  const isDocked = useMemo(
    () => dockedWidgets.includes(widgetId),
    [dockedWidgets, widgetId]
  );

  const isMinimized = useMemo(
    () => minimizedWidgets.includes(widgetId),
    [minimizedWidgets, widgetId]
  );

  // Memoizar callbacks com widgetId fixo
  const handleToggleDock = useCallback(
    () => toggleDock(widgetId),
    [toggleDock, widgetId]
  );

  const handleToggleMinimize = useCallback(
    () => toggleMinimize(widgetId),
    [toggleMinimize, widgetId]
  );

  const handleUpdateData = useCallback(
    (newData) => updateWidgetData(widgetId, newData),
    [updateWidgetData, widgetId]
  );

  // Retornar objeto memoizado - data é obtido via getter (não causa re-render)
  return useMemo(() => ({
    isDocked,
    isMinimized,
    getData: () => getWidgetData(widgetId), // Getter lazy para evitar re-renders
    toggleDock: handleToggleDock,
    toggleMinimize: handleToggleMinimize,
    updateData: handleUpdateData,
  }), [isDocked, isMinimized, getWidgetData, widgetId, handleToggleDock, handleToggleMinimize, handleUpdateData]);
};

export default WidgetDockContext;
